import {
  dayKeysOf, dayOrder, findTalent, matches, sessionMeta, tiersFor,
} from '@ce/core';
import { trackColor } from '@ce/theme';
import { useTheme } from '../ThemeProvider';
import {
  btn, divider, kicker, matchStyle, pill, portrait, saveStyle, settle, t,
} from '../styles';
import type { ScreenProps } from './types';

export function Talent({ bundle, state, dispatch }: ScreenProps) {
  const roster = bundle.talent.filter(
    (person) => state.talentFilter === 'All' || person.fandoms.includes(state.talentFilter),
  );
  const cur = bundle.event.currency;

  return (
    <div className="stagger">
      <div
        className="scroll"
        style={{ display: 'flex', gap: 6, overflowX: 'auto', margin: '0 -20px 16px', padding: '2px 20px', position: 'relative' }}
      >
        {bundle.talentFilters.map((filter) => (
          <button
            key={filter}
            type="button"
            onClick={() => dispatch({ type: 'setTalentFilter', filter })}
            aria-pressed={state.talentFilter === filter}
            style={pill(state.talentFilter === filter)}
          >
            {filter}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gap: 8, position: 'relative' }}>
        {roster.map((person, i) => {
          const match = matches(state.interests, person.fandoms);
          const from = Math.min(person.prices.photo, person.prices.auto);
          return (
            <button
              key={person.id}
              type="button"
              onClick={() => dispatch({ type: 'openTalent', id: person.id })}
              style={{
                display: 'flex', gap: 13, alignItems: 'center', textAlign: 'left',
                padding: '11px 13px', borderRadius: 10, background: 'var(--color-surface)',
                border: 0, color: 'var(--color-text)', cursor: 'pointer',
                boxShadow: 'var(--shadow-sm)', ...settle(i),
              }}
            >
              <div
                style={{
                  flex: 'none', width: 54, height: 54, borderRadius: 8,
                  overflow: 'hidden', background: 'var(--color-neutral-900)',
                }}
              >
                <div style={portrait(person.photoUrl, 20)} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: '500 15px/1.2 var(--font-body)' }}>{person.name}</div>
                <div style={{ fontSize: 12, lineHeight: 1.4, color: t(0.55), marginTop: 4 }}>
                  {person.role}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 7, flexWrap: 'wrap' }}>
                  <span
                    style={{
                      display: 'inline-flex', alignItems: 'center', fontSize: 10,
                      letterSpacing: '0.02em', padding: '3px 10px', borderRadius: 6,
                      background: 'var(--color-neutral-800)', color: 'var(--color-neutral-100)',
                    }}
                  >
                    {person.days}
                  </span>
                  <span style={matchStyle(match)}>Match</span>
                </div>
              </div>
              <div style={{ fontSize: 11, color: t(0.4), flex: 'none', textAlign: 'right' }}>
                from {cur}{from}
              </div>
            </button>
          );
        })}
      </div>
      <div style={{ height: 26 }} />
    </div>
  );
}

export function TalentDetail({ bundle, state, dispatch }: ScreenProps) {
  const theme = useTheme();
  const person = findTalent(bundle, state.talentId);
  const tiers = tiersFor(person);
  const sessions = bundle.sessions.filter((s) => s.who.includes(person.id));
  const cur = bundle.event.currency;
  const firstDay = dayKeysOf(person.days, dayOrder(bundle))[0] ?? bundle.days[0].key;

  return (
    <div className="stagger">
      <button
        type="button"
        onClick={() => dispatch({ type: 'go', screen: 'talent' })}
        style={btn('ghost', { fontSize: 12.5, margin: '0 0 12px -2px' })}
      >
        ← All talent
      </button>

      <div style={{ display: 'flex', gap: 15, alignItems: 'flex-start' }}>
        <div
          style={{
            flex: 'none', width: 118, height: 152, borderRadius: 10,
            background: 'var(--color-surface)', position: 'relative', overflow: 'hidden',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          <div style={portrait(person.photoUrl, 20)} />
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(to top, rgba(var(--color-bg-rgb), 0.5), transparent 55%)',
            }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontSize: 22, margin: 0, letterSpacing: '-0.02em', fontWeight: 500 }}>
            {person.name}
          </h3>
          <div style={{ fontSize: 12.5, lineHeight: 1.45, color: t(0.58), marginTop: 6 }}>
            {person.role}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            <span
              style={{
                display: 'inline-flex', fontSize: 10, padding: '3px 10px', borderRadius: 6,
                background: 'var(--color-accent-800)', color: 'var(--color-accent-100)',
              }}
            >
              {person.fandoms[0]}
            </span>
            <span
              style={{
                display: 'inline-flex', fontSize: 10, padding: '3px 10px', borderRadius: 6,
                background: 'var(--color-neutral-800)', color: 'var(--color-neutral-100)',
              }}
            >
              {person.days}
            </span>
          </div>
        </div>
      </div>

      <p style={{ fontSize: 13, lineHeight: 1.65, color: t(0.7), margin: '18px 0 0' }}>{person.bio}</p>

      <div style={{ display: 'grid', gap: 9, marginTop: 20 }}>
        {tiers.slice(0, 3).map((tier) => (
          <button
            key={tier.key}
            type="button"
            onClick={() =>
              dispatch({ type: 'openBooking', talentId: person.id, tier: tier.key, day: firstDay })
            }
            style={{
              display: 'flex', alignItems: 'center', gap: 13, textAlign: 'left', padding: 14,
              borderRadius: 10, border: '1px solid var(--color-accent-700)', background: 'transparent',
              color: 'var(--color-text)', cursor: 'pointer',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ font: '500 14.5px/1.2 var(--font-body)' }}>{tier.label}</div>
              <div style={{ fontSize: 11.5, lineHeight: 1.45, color: t(0.5), marginTop: 4 }}>
                {tier.note}
              </div>
            </div>
            <div
              style={{
                font: '500 16px/1 var(--font-body)', color: 'var(--color-accent-300)', flex: 'none',
              }}
            >
              {cur}{tier.price}
            </div>
          </button>
        ))}
      </div>

      <div style={{ ...kicker(), margin: '26px 0 11px' }}>Appearing on</div>
      <div style={{ display: 'grid', gap: 8 }}>
        {sessions.map((s) => {
          const on = state.saved.includes(s.id);
          return (
            <div
              key={s.id}
              style={{
                display: 'flex', gap: 12, padding: '12px 14px', borderRadius: 10,
                background: 'var(--color-surface)',
              }}
            >
              <div
                style={{
                  width: 2, alignSelf: 'stretch', borderRadius: 2,
                  background: trackColor(theme, s.track), flex: 'none',
                }}
              />
              <button
                type="button"
                onClick={() => dispatch({ type: 'openSession', id: s.id })}
                style={{
                  flex: 1, minWidth: 0, textAlign: 'left', border: 0, padding: 0,
                  background: 'transparent', color: 'var(--color-text)', cursor: 'pointer',
                }}
              >
                <div style={{ font: '500 14px/1.25 var(--font-body)' }}>{s.title}</div>
                <div style={{ fontSize: 11.5, color: t(0.52), marginTop: 4 }}>
                  {sessionMeta(bundle, s)}
                </div>
              </button>
              <button
                type="button"
                aria-label={on ? 'Unstar' : 'Star'}
                onClick={() => dispatch({ type: 'toggleSave', id: s.id })}
                style={saveStyle(on)}
              >
                {on ? '★' : '☆'}
              </button>
            </div>
          );
        })}
      </div>
      <div style={{ height: 26, borderColor: divider }} />
    </div>
  );
}
